/**
 * Which vector layers of a run the user switched off (MSLP, wind barbs,
 * fronts). The switch is a device preference kept in the `mapPreferences`
 * slice, by a key built from the layer's type and label rather than by its
 * config-layer index: MSLP can be layer 0 of one config and layer 1 of the
 * next, and a layer switched off stays off on every forecast map and every
 * visit. Shared so the mobile map, once it has the switches, reads the same
 * preference.
 *
 * The label is the config layer's `display_name`, set per layer in the
 * backend presets ("MSLP", "10m wind", "Fronts"), not translated. A builder
 * config naming its layer otherwise ("Pressure") has a switch of its own,
 * and two layers of one config sharing a type and label share a switch
 * (`display_name` is not unique per config).
 */
import type {ForecastMapVectorLayerSpec} from '../../api/types';

/** The key a layer's switch is remembered by. */
export const forecastVectorLayerKey = (
  spec: Pick<ForecastMapVectorLayerSpec, 'type' | 'label'>,
): string => `${spec.type}:${spec.label}`;

/**
 * The layers hidden until the user switches them on: the 10m wind barbs,
 * which the wind particles make redundant (Vincent, 2026-10-20). Every
 * preset labels its barbs layer "10m wind". Each app's persist migration
 * (mobile 38, webapp 10) wrote the key into the lists stored before this
 * default existed.
 */
export const DEFAULT_HIDDEN_FORECAST_VECTOR_LAYERS: readonly string[] = [
  forecastVectorLayerKey({type: 'barbs', label: '10m wind'}),
];

/**
 * The config-layer indexes of this run's `layers` the user switched off,
 * for `forecastVectorLayerSpecs`' `disabledLayers`.
 */
export const disabledForecastVectorLayers = (
  layers: readonly ForecastMapVectorLayerSpec[],
  hiddenKeys: readonly string[],
): number[] =>
  layers
    .filter((spec) => hiddenKeys.includes(forecastVectorLayerKey(spec)))
    .map((spec) => spec.index);
